// parallax.js - Sistema unificato per tutti gli effetti parallax
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// ============================================================================
// PARALLAX TYPES CONFIGURATION
// ============================================================================

const PARALLAX_TYPES = {
  HOME_PARALLAX: 'home-parallax',
  IMAGE_PARALLAX: 'image-parallax',
  CARD_PARALLAX: 'card-parallax'
};

// ============================================================================
// HOME PARALLAX WIDGET (GSAP + ScrollTrigger)
// ============================================================================

class HomeParallaxWidget {
  constructor(widgetId) {
    this.widgetId = widgetId;
    this.type = PARALLAX_TYPES.HOME_PARALLAX;
    this.blocks = document.querySelectorAll(`[data-widget-id="${widgetId}"]`);
    this.numbers = document.querySelectorAll(`[data-widget-id="${widgetId}"] .parallax-number`);
    this.backgrounds = document.querySelectorAll(`[data-widget-id="${widgetId}"] .parallax-background`);
    this.scrollTriggers = [];
    
    // Traccia l'istanza globalmente
    this.trackInstance();
    this.init();
  }

  trackInstance() {
    if (!window.parallaxInstances) {
      window.parallaxInstances = {};
    }
    if (!window.parallaxInstances[this.type]) {
      window.parallaxInstances[this.type] = {};
    }
    window.parallaxInstances[this.type][this.widgetId] = this;
  }

  init() {
    // Verifica che gli elementi siano ancora nel DOM
    this.blocks = document.querySelectorAll(`[data-widget-id="${this.widgetId}"]`);
    this.numbers = document.querySelectorAll(`[data-widget-id="${this.widgetId}"] .parallax-number`);
    this.backgrounds = document.querySelectorAll(`[data-widget-id="${this.widgetId}"] .parallax-background`);
    
    if (this.blocks.length === 0) {
      console.warn(`⚠️ HomeParallax widget ${this.widgetId}: nessun blocco trovato nel DOM`);
      return;
    }
    
    this.setupStaticNumbers();
    this.setupParallaxEffect();
    this.setupHoverEffects();
    ScrollTrigger.refresh();
  }

  setupStaticNumbers() {
    this.numbers.forEach(elem => {
      const targetVal = elem.getAttribute('data-target');
      if (targetVal != null) {
        const num = this.extractNumericValue(targetVal);
        elem.textContent = this.formatNumber(num);
      }
    });
  }

  setupParallaxEffect() {
    const parallaxValues = [15, 10, 15, 10];

    // Verifica che ci siano blocchi da processare
    if (this.blocks.length === 0) {
      console.warn(`⚠️ HomeParallax widget ${this.widgetId}: nessun blocco da processare`);
      return;
    }

    this.blocks.forEach((block, index) => {
      // Verifica che il blocco sia nel DOM
      if (!block.isConnected) {
        console.warn(`⚠️ Blocco ${index} non è nel DOM, skip`);
        return;
      }

      // Reset delle proprietà CSS per assicurarsi che siano corrette
      gsap.set(block, { 
        clearProps: "transform,opacity",
        opacity: 0,
        y: 0
      });

      const parallaxTween = gsap.to(block, {
        yPercent: parallaxValues[index] || -30,
        ease: "none",
        scrollTrigger: {
          trigger: block,
          start: "top bottom",
          end: "bottom top",
          scrub: 1,
          refreshPriority: -1, // Priorità bassa per refresh
        }
      });

      const fadeTween = gsap.fromTo(block,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: block,
            start: "top 90%",
            end: "top 70%",
            toggleActions: "play none none reverse",
            refreshPriority: -1, // Priorità bassa per refresh
          }
        }
      );

      this.scrollTriggers.push(parallaxTween.scrollTrigger, fadeTween.scrollTrigger);
    });

    // Forza un refresh immediato dei ScrollTrigger
    if (gsap && gsap.ScrollTrigger) {
      gsap.ScrollTrigger.refresh();
    }
  }

  setupHoverEffects() {
    this.blocks.forEach(block => {
      const content = block.querySelector('.parallax-content');

      block.addEventListener('mouseenter', () => {
        gsap.to(content, { y: -8, duration: 0.4, ease: "power2.out" });
      });

      block.addEventListener('mouseleave', () => {
        gsap.to(content, { y: 0, duration: 0.4, ease: "power2.out" });
      });
    });
  }

  extractNumericValue(val) {
    if (typeof val === 'string') return parseInt(val.replace(/[^\d]/g, ''), 10) || 0;
    return parseInt(val, 10) || 0;
  }

  formatNumber(num) {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }

  updateNumber(i, newVal) {
    const el = this.numbers[i];
    if (!el) return;
    const num = this.extractNumericValue(newVal);
    el.setAttribute('data-target', newVal);
    el.textContent = this.formatNumber(num);
  }

  updateText(i, txt) {
    const el = this.blocks[i]?.querySelector('.parallax-text');
    if (!el) return;
    el.textContent = txt;
  }

  updateBackground(i, url) {
    const bg = this.blocks[i]?.querySelector('.parallax-background');
    if (!bg) return;
    bg.style.backgroundImage = `url('${url}')`;
  }

  destroy() {
    this.scrollTriggers.forEach(trigger => {
      if (trigger && typeof trigger.kill === 'function') {
        trigger.kill();
      }
    });
    this.scrollTriggers = [];
    
    // Kill ScrollTrigger orfani
    if (gsap && gsap.ScrollTrigger && gsap.ScrollTrigger.getAll) {
      gsap.ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger && this.blocks.length > 0 && this.blocks[0].contains(trigger.trigger)) {
          trigger.kill();
        }
      });
    }
    
    // Rimuovi dall'istanza
    if (window.parallaxInstances?.[this.type]?.[this.widgetId]) {
      delete window.parallaxInstances[this.type][this.widgetId];
    }
    
    this.blocks.forEach(b => b.replaceWith(b.cloneNode(true)));
    //console.log(`🗑️ HomeParallax widget ${this.widgetId} distrutto`);
  }
}

// ============================================================================
// IMAGE PARALLAX WIDGET (RequestAnimationFrame)
// ============================================================================

class ImageParallaxWidget {
  constructor(elementId) {
    this.elementId = elementId;
    this.type = PARALLAX_TYPES.IMAGE_PARALLAX;
    this.element = document.getElementById(elementId);
    this.parallaxImage = null;
    this.speed = 0;
    this.direction = 'vertical';
    this.trigger = 'scroll';
    this.targetOffset = 0;
    this.currentOffset = 0;
    this.animationId = null;
    
    this.trackInstance();
    this.init();
  }

  trackInstance() {
    if (!window.parallaxInstances) {
      window.parallaxInstances = {};
    }
    if (!window.parallaxInstances[this.type]) {
      window.parallaxInstances[this.type] = {};
    }
    window.parallaxInstances[this.type][this.elementId] = this;
  }

  init() {
    if (!this.element) {
      console.warn('🎭 ImageParallax: elemento non trovato con ID:', this.elementId);
      return;
    }
    
    this.parallaxImage = this.element.querySelector('.parallax-image');
    if (!this.parallaxImage) {
      console.warn('🎭 ImageParallax: .parallax-image non trovato in:', this.elementId);
      return;
    }
    
    this.speed = parseFloat(this.element.dataset.parallaxSpeed) || 1;
    this.direction = this.element.dataset.parallaxDirection || 'vertical';
    this.trigger = this.element.dataset.parallaxTrigger || 'scroll';
    
    this.setupEventListeners();
    //console.log('🎭 ImageParallax: inizializzazione completata per', this.elementId);
  }

  setupEventListeners() {
    if (this.trigger === 'scroll') {
      this.updateParallax = this.updateParallax.bind(this);
      window.addEventListener('scroll', this.updateParallax);
    } else if (this.trigger === 'mouse') {
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.element.addEventListener('mousemove', this.handleMouseMove);
    }
  }

  updateParallax() {
    const rect = this.element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    
    const parallaxOffset = Math.max(-200, Math.min(200, (rect.top / viewportHeight) * this.speed * 80));
    this.targetOffset = parallaxOffset;
    
    if (!this.animationId) {
      this.animateParallax();
    }
  }

  handleMouseMove(e) {
    const rect = this.element.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    
    const moveX = (x - 1.5) * this.speed * 50;
    const moveY = (y - 1.5) * this.speed * 50;
    
    let transform = '';
    
    if (this.direction === 'horizontal' || this.direction === 'both') {
      transform += `translateX(${moveX}px)`;
    }
    if (this.direction === 'vertical' || this.direction === 'both') {
      transform += ` translateY(${moveY}px)`;
    }
    
    this.parallaxImage.style.transform = transform;
  }

  animateParallax() {
    const easing = 0.08;
    this.currentOffset += (this.targetOffset - this.currentOffset) * easing;
    
    let transform = '';
    
    if (this.direction === 'vertical' || this.direction === 'both') {
      transform += `translateY(${this.currentOffset}px)`;
    }
    if (this.direction === 'horizontal' || this.direction === 'both') {
      const horizontalOffset = this.currentOffset * 0.5;
      transform += ` translateX(${horizontalOffset}px)`;
    }
    
    this.parallaxImage.style.transform = transform;
    
    if (Math.abs(this.targetOffset - this.currentOffset) > 0.1) {
      this.animationId = requestAnimationFrame(() => this.animateParallax());
    } else {
      this.animationId = null;
    }
  }

  destroy() {
    if (this.trigger === 'scroll' && this.updateParallax) {
      window.removeEventListener('scroll', this.updateParallax);
    } else if (this.trigger === 'mouse' && this.handleMouseMove) {
      this.element.removeEventListener('mousemove', this.handleMouseMove);
    }
    
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    
    // Rimuovi dall'istanza
    if (window.parallaxInstances?.[this.type]?.[this.elementId]) {
      delete window.parallaxInstances[this.type][this.elementId];
    }
    
    //console.log(`🗑️ ImageParallax widget ${this.elementId} distrutto`);
  }
}

// ============================================================================
// CARD PARALLAX WIDGET (GSAP + ScrollTrigger per card)
// ============================================================================

class CardParallaxWidget {
  constructor(gridId) {
    this.gridId = gridId;
    this.type = PARALLAX_TYPES.CARD_PARALLAX;
    this.grid = document.getElementById(gridId);
    this.cards = [];
    this.scrollTriggers = [];
    this.animatedElements = new Set();
    
    this.trackInstance();
    this.init();
  }

  trackInstance() {
    if (!window.parallaxInstances) {
      window.parallaxInstances = {};
    }
    if (!window.parallaxInstances[this.type]) {
      window.parallaxInstances[this.type] = {};
    }
    window.parallaxInstances[this.type][this.gridId] = this;
  }

  init() {
    if (!this.grid) {
      console.warn('🎭 CardParallax: grid non trovato con ID:', this.gridId);
      return;
    }

    this.cards = Array.from(this.grid.querySelectorAll('.agricole-card'));
    if (this.cards.length === 0) {
      console.warn('🎭 CardParallax: nessuna card trovata in:', this.gridId);
      return;
    }

    this.setupCardAnimations();
    //console.log(`🎭 CardParallax: inizializzazione completata per ${this.gridId} con ${this.cards.length} card`);
  }

  setupCardAnimations() {
    const isScaleEffect = this.grid.classList.contains('scale-effect');
    
    this.cards.forEach((card, index) => {
      // Skip if already animated
      if (this.animatedElements.has(card)) {
        return;
      }

      // Reset delle proprietà CSS
      gsap.set(card, { 
        clearProps: "transform,opacity,visibility",
        opacity: 0,
        visibility: 'visible',
        y: 80
      });

      // Calcola delay basato sulla posizione
      const delay = parseFloat(card.dataset.animationDelay) || 0;
      const staggerDelay = index * 0.1; // Stagger tra le card
      const totalDelay = delay + staggerDelay;

      // Animazione di entrata
      const enterTween = gsap.to(card, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        delay: totalDelay,
        ease: "power2.out",
        force3D: true,
        onComplete: () => {
          this.animatedElements.add(card);
          if (card && card.style) {
            card.style.willChange = 'auto';
          }
        }
      });

      // ScrollTrigger per l'attivazione
      const scrollTrigger = ScrollTrigger.create({
        trigger: card,
        start: "top 90%",
        end: "bottom 10%",
        toggleActions: "play none none none",
        animation: enterTween,
        refreshPriority: -1
      });

      this.scrollTriggers.push(scrollTrigger);
    });

    // Refresh ScrollTrigger
    if (gsap && gsap.ScrollTrigger) {
      gsap.ScrollTrigger.refresh();
    }
  }

  destroy() {
    this.scrollTriggers.forEach(trigger => {
      if (trigger && typeof trigger.kill === 'function') {
        trigger.kill();
      }
    });
    this.scrollTriggers = [];
    this.animatedElements.clear();
    
    // Rimuovi dall'istanza
    if (window.parallaxInstances?.[this.type]?.[this.gridId]) {
      delete window.parallaxInstances[this.type][this.gridId];
    }
    
    //console.log(`🗑️ CardParallax widget ${this.gridId} distrutto`);
  }
}

// ============================================================================
// UNIFIED INITIALIZATION FUNCTIONS
// ============================================================================

// Inizializza un singolo widget homeParallax
export function initHomeParallaxWidget(widgetId) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new HomeParallaxWidget(widgetId));
  } else {
    new HomeParallaxWidget(widgetId);
  }
}

// Inizializza un singolo widget imageParallax
export function initImageParallaxWidget(elementId) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new ImageParallaxWidget(elementId));
  } else {
    new ImageParallaxWidget(elementId);
  }
}

// Inizializza tutti i widget homeParallax
export function initAllHomeParallaxWidgets() {
  const elements = document.querySelectorAll('[data-widget-id^="home-parallax-"]');
  //console.log(`🎭 initAllHomeParallaxWidgets: trovati ${elements.length} elementi HomeParallax`);
  
  const ids = new Set();
  elements.forEach(el => {
    ids.add(el.getAttribute('data-widget-id'));
  });
  
  //console.log(`🎭 Widget IDs trovati:`, Array.from(ids));
  
  ids.forEach(id => {
    // Durante le transizioni Swup, forziamo sempre la reinizializzazione
    // perché gli elementi DOM vengono sostituiti
    const existingInstance = window.parallaxInstances?.[PARALLAX_TYPES.HOME_PARALLAX]?.[id];
    if (existingInstance) {
      //console.log(`🔄 Reinizializzazione forzata HomeParallax widget: ${id}`);
      existingInstance.destroy();
    } else {
      //console.log(`🔄 Inizializzazione HomeParallax widget: ${id}`);
    }
    
    // Debug: verifica che gli elementi esistano nel DOM
    const domElements = document.querySelectorAll(`[data-widget-id="${id}"]`);
    //console.log(`🔍 Debug ${id}: trovati ${domElements.length} elementi nel DOM`);
    
    initHomeParallaxWidget(id);
  });

  // Refresh forzato dopo un breve delay per assicurarsi che tutto sia pronto
  setTimeout(() => {
    if (gsap && gsap.ScrollTrigger) {
      gsap.ScrollTrigger.refresh();
      //console.log(`🔄 ScrollTrigger refresh forzato per HomeParallax`);
      
      // Forza l'attivazione dei ScrollTrigger se i blocchi sono visibili
      elements.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        
        if (isVisible) {
          //console.log(`🎭 Blocco ${index} è visibile, forzo attivazione ScrollTrigger`);
          // Trigger manuale per blocchi già visibili
          gsap.ScrollTrigger.refresh();
        }
      });
    }
  }, 200);
}

// Inizializza tutti i widget imageParallax
export function initAllImageParallaxWidgets() {
  const elements = document.querySelectorAll('[data-parallax-speed]');
  //console.log('🎭 initAllImageParallaxWidgets: trovati', elements.length, 'elementi parallax');
  
  elements.forEach((element, index) => {
    if (element.id) {
      // Durante le transizioni Swup, forziamo sempre la reinizializzazione
      const existingInstance = window.parallaxInstances?.[PARALLAX_TYPES.IMAGE_PARALLAX]?.[element.id];
      if (existingInstance) {
        //console.log(`🔄 Reinizializzazione forzata ImageParallax ${index + 1}:`, element.id);
        existingInstance.destroy();
      } 
      // else {
      //   console.log(`🎭 Inizializzazione ImageParallax ${index + 1}:`, element.id, {
      //     speed: element.dataset.parallaxSpeed,
      //     direction: element.dataset.parallaxDirection,
      //     trigger: element.dataset.parallaxTrigger
      //   });
      // }
      initImageParallaxWidget(element.id);
    } else {
      console.warn('🎭 Elemento parallax senza ID:', element);
    }
  });
}

// Inizializza un singolo widget cardParallax
export function initCardParallaxWidget(gridId) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new CardParallaxWidget(gridId));
  } else {
    new CardParallaxWidget(gridId);
  }
}

// Inizializza tutti i widget cardParallax
export function initAllCardParallaxWidgets() {
  const grids = document.querySelectorAll('.effect-card-animate');
  //console.log(`🎭 initAllCardParallaxWidgets: trovati ${grids.length} grid di card`);
  
  grids.forEach((grid, index) => {
    const gridId = grid.id || `card-grid-${index}`;
    
    // Se non ha ID, assegnagliene uno
    if (!grid.id) {
      grid.id = gridId;
    }
    
    // Durante le transizioni Swup, forziamo sempre la reinizializzazione
    const existingInstance = window.parallaxInstances?.[PARALLAX_TYPES.CARD_PARALLAX]?.[gridId];
    if (existingInstance) {
      //console.log(`🔄 Reinizializzazione forzata CardParallax widget: ${gridId}`);
      existingInstance.destroy();
    } else {
      //console.log(`🔄 Inizializzazione CardParallax widget: ${gridId}`);
    }
    initCardParallaxWidget(gridId);
  });
}

// Inizializza TUTTI i widget parallax (unificato)
export function initAllParallaxWidgets() {
  initAllHomeParallaxWidgets();
  initAllImageParallaxWidgets();
  initAllCardParallaxWidgets();
}

// ============================================================================
// CLEANUP FUNCTIONS
// ============================================================================

// Distruggi tutti i widget homeParallax
export function destroyAllHomeParallaxWidgets() {
  if (window.parallaxInstances?.[PARALLAX_TYPES.HOME_PARALLAX]) {
    Object.keys(window.parallaxInstances[PARALLAX_TYPES.HOME_PARALLAX]).forEach(widgetId => {
      try {
        window.parallaxInstances[PARALLAX_TYPES.HOME_PARALLAX][widgetId].destroy();
      } catch (e) {
        console.warn(`Errore nella distruzione del HomeParallax widget ${widgetId}:`, e);
      }
    });
    window.parallaxInstances[PARALLAX_TYPES.HOME_PARALLAX] = {};
    //console.log('🗑️ Tutti i HomeParallax widget sono stati distrutti');
  }
}

// Distruggi tutti i widget imageParallax
export function destroyAllImageParallaxWidgets() {
  if (window.parallaxInstances?.[PARALLAX_TYPES.IMAGE_PARALLAX]) {
    Object.keys(window.parallaxInstances[PARALLAX_TYPES.IMAGE_PARALLAX]).forEach(widgetId => {
      try {
        window.parallaxInstances[PARALLAX_TYPES.IMAGE_PARALLAX][widgetId].destroy();
      } catch (e) {
        console.warn(`Errore nella distruzione dell'ImageParallax widget ${widgetId}:`, e);
      }
    });
    window.parallaxInstances[PARALLAX_TYPES.IMAGE_PARALLAX] = {};
    //console.log('🗑️ Tutti gli ImageParallax widget sono stati distrutti');
  }
}

// Distruggi tutti i widget cardParallax
export function destroyAllCardParallaxWidgets() {
  if (window.parallaxInstances?.[PARALLAX_TYPES.CARD_PARALLAX]) {
    Object.keys(window.parallaxInstances[PARALLAX_TYPES.CARD_PARALLAX]).forEach(gridId => {
      try {
        window.parallaxInstances[PARALLAX_TYPES.CARD_PARALLAX][gridId].destroy();
      } catch (e) {
        console.warn(`Errore nella distruzione del CardParallax widget ${gridId}:`, e);
      }
    });
    window.parallaxInstances[PARALLAX_TYPES.CARD_PARALLAX] = {};
    //console.log('🗑️ Tutti i CardParallax widget sono stati distrutti');
  }
}

// Distruggi TUTTI i widget parallax
export function destroyAllParallaxWidgets() {
  destroyAllHomeParallaxWidgets();
  destroyAllImageParallaxWidgets();
  destroyAllCardParallaxWidgets();
}

// ============================================================================
// DEBUG FUNCTIONS
// ============================================================================

// Debug per tutti i widget parallax
export function debugAllParallaxWidgets() {
  //console.log('🔍 Debug Parallax Widgets:');
  // console.log('- HomeParallax nel DOM:', document.querySelectorAll('[data-widget-id^="home-parallax-"]').length);
  // console.log('- ImageParallax nel DOM:', document.querySelectorAll('[data-parallax-speed]').length);
  // console.log('- CardParallax nel DOM:', document.querySelectorAll('.effect-card-animate').length);
  // console.log('- ScrollTrigger totali:', gsap?.ScrollTrigger?.getAll?.()?.length || 0);
  
  if (window.parallaxInstances) {
    Object.keys(window.parallaxInstances).forEach(type => {
      const instances = window.parallaxInstances[type];
      console.log(`  - ${type}:`, Object.keys(instances).length, 'istanze');
      Object.keys(instances).forEach(id => {
        const widget = instances[id];
        console.log(`    - ${id}:`, {
          type: widget.type,
          blocks: widget.blocks?.length || 'N/A',
          scrollTriggers: widget.scrollTriggers?.length || 'N/A'
        });
      });
    });
  }
}

// Debug per tipo specifico
export function debugParallaxWidgetsByType(type) {
  //console.log(`🔍 Debug ${type} Widgets:`);
  if (window.parallaxInstances?.[type]) {
    Object.keys(window.parallaxInstances[type]).forEach(id => {
      const widget = window.parallaxInstances[type][id];
      // console.log(`  - ${id}:`, {
      //   type: widget.type,
      //   blocks: widget.blocks?.length || 'N/A',
      //   scrollTriggers: widget.scrollTriggers?.length || 'N/A'
      // });
    });
  } 
  // else {
  //   console.log(`  - Nessuna istanza di ${type} trovata`);
  // }
}

// ============================================================================
// AUTO-INITIALIZATION
// ============================================================================

// Auto-inizializzazione al DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllParallaxWidgets);
} else {
  initAllParallaxWidgets();
}

// Observer per elementi aggiunti dinamicamente
if (window.MutationObserver) {
  const obs = new MutationObserver(muts => {
    muts.forEach(m => {
      m.addedNodes.forEach(n => {
        if (n.nodeType === 1) {
          // HomeParallax widgets
          if (n.matches?.('[data-widget-id^="home-parallax-"]')) {
            initHomeParallaxWidget(n.getAttribute('data-widget-id'));
          }
          n.querySelectorAll?.('[data-widget-id^="home-parallax-"]').forEach(el => {
            initHomeParallaxWidget(el.getAttribute('data-widget-id'));
          });
          
          // ImageParallax widgets
          if (n.matches?.('[data-parallax-speed]') && n.id) {
            initImageParallaxWidget(n.id);
          }
          n.querySelectorAll?.('[data-parallax-speed]').forEach(el => {
            if (el.id) initImageParallaxWidget(el.id);
          });
          
          // CardParallax widgets
          if (n.matches?.('.effect-card-animate')) {
            const gridId = n.id || `card-grid-${Date.now()}`;
            if (!n.id) n.id = gridId;
            initCardParallaxWidget(gridId);
          }
          n.querySelectorAll?.('.effect-card-animate').forEach(el => {
            const gridId = el.id || `card-grid-${Date.now()}`;
            if (!el.id) el.id = gridId;
            initCardParallaxWidget(gridId);
          });
        }
      });
    });
  });
  obs.observe(document.body, { childList: true, subtree: true });
}

// ============================================================================
// EXPORTS
// ============================================================================

export { HomeParallaxWidget, ImageParallaxWidget, CardParallaxWidget, PARALLAX_TYPES };

// Backward compatibility exports (alias per compatibilità con il vecchio sistema)
export const initParallaxImg = initImageParallaxWidget;
export const initAllParallaxImgs = initAllImageParallaxWidgets;
export const cleanupAllParallaxImgs = destroyAllImageParallaxWidgets;
